import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Upload, Video as VideoIcon, Check, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

// Chunk size for large uploads (1MB)
const CHUNK_SIZE = 1024 * 1024;

const VideoUploadPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [caseId, setCaseId] = useState('');
  const [partnerId, setPartnerId] = useState('');
  const [videoFile, setVideoFile] = useState(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');

  useEffect(() => {
    const cid = searchParams.get('case_id');
    const pid = searchParams.get('partner_id') || localStorage.getItem('partner_id');
    
    if (!cid || !pid) {
      navigate('/portal/dashboard');
      return;
    }
    
    setCaseId(cid);
    setPartnerId(pid);
    loadVideo(cid);
  }, [searchParams]);

  const loadVideo = async (cid) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cases/${cid}`);
      const data = await response.json();
      
      if (data.video_url) {
        setVideoUrl(data.video_url);
      }
    } catch (error) {
      console.error('Load video failed:', error);
    }
  };

  // Chunked upload for large files - sends binary chunks directly
  const uploadInChunks = async (file) => {
    try {
      // 1. Initialize upload
      const initFormData = new FormData();
      initFormData.append('case_id', caseId);
      initFormData.append('filename', file.name);
      initFormData.append('total_size', file.size.toString());
      
      const initRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/uploads/video/init`, {
        method: 'POST',
        body: initFormData
      });
      
      if (!initRes.ok) {
        const err = await initRes.json();
        throw new Error(err.detail || 'Upload-Initialisierung fehlgeschlagen');
      }
      
      const { upload_id } = await initRes.json();
      
      // 2. Upload chunks as binary blobs
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
      
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);
        
        const chunkFormData = new FormData();
        chunkFormData.append('upload_id', upload_id);
        chunkFormData.append('chunk_index', i.toString());
        chunkFormData.append('chunk_file', chunk, `chunk_${i}`);
        
        const chunkRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/uploads/video/chunk`, {
          method: 'POST',
          body: chunkFormData
        });
        
        if (!chunkRes.ok) {
          const errData = await chunkRes.json().catch(() => ({}));
          throw new Error(errData.detail || 'Chunk-Upload fehlgeschlagen');
        }
        
        setUploadProgress(Math.round(((i + 1) / totalChunks) * 100));
      }
      
      // 3. Complete upload
      const completeFormData = new FormData();
      completeFormData.append('upload_id', upload_id);
      
      const completeRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/uploads/video/complete`, {
        method: 'POST',
        body: completeFormData
      });
      
      if (!completeRes.ok) {
        throw new Error('Upload-Abschluss fehlgeschlagen');
      }
      
      return await completeRes.json();
    } catch (err) {
      throw err;
    }
  };

  const handleVideoUpload = async (file) => {
    if (!file) return;
    
    if (!file.type.startsWith('video/')) {
      setError('Bitte nur Video-Dateien hochladen');
      return;
    }
    
    // Check file size (max 100MB)
    if (file.size > 100 * 1024 * 1024) {
      setError('Video zu groß. Maximale Größe: 100MB');
      return;
    }
    
    setUploading(true);
    setError('');
    setUploadProgress(0);
    
    try {
      let result;
      
      // Use chunked upload for files larger than 2MB
      if (file.size > 2 * 1024 * 1024) {
        result = await uploadInChunks(file);
      } else {
        // Direct upload for small files
        const formData = new FormData();
        formData.append('video', file);
        formData.append('case_id', caseId);
        
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/uploads/video`, {
          method: 'POST',
          body: formData
        });
        
        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.detail || 'Upload fehlgeschlagen');
        }
        
        result = await response.json();
      }
      
      if (result.success) {
        setVideoFile(file);
        setVideoUrl(result.video_url);
      }
    } catch (err) {
      setError(err.message || 'Video-Upload fehlgeschlagen');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleWeiter = () => {
    navigate(`/portal/uebersicht-submit?case_id=${caseId}&partner_id=${partnerId}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 py-6">
        <div className="max-w-7xl mx-auto px-6">
          <h1 className="text-2xl font-bold text-gray-900">Video-Rundgang</h1>
          <p className="text-gray-600 mt-1">Pflicht: Einmal um das Fahrzeug herum filmen</p>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Video-Regeln */}
        <div className="mb-8 p-6 bg-blue-50 border-2 border-blue-200 rounded-xl">
          <h3 className="font-bold text-blue-900 mb-4">🎥 Video-Anforderungen</h3>
          <ul className="space-y-2 text-sm text-blue-800">
            <li>✓ Einmal 360° um das Fahrzeug herum laufen</li>
            <li>✓ 2–3 Sekunden nah auf den Schaden halten</li>
            <li>✓ Langsam filmen, nicht verwackeln</li>
            <li>✓ Bei Tageslicht, hell genug</li>
            <li>✓ Querformat empfohlen</li>
          </ul>
          <p className="text-xs text-blue-700 mt-3">
            Das Video hilft uns, den Gesamtzustand schnell zu erfassen.
          </p>
        </div>

        {/* Upload Area */}
        <div className="bg-white border-2 border-dashed border-gray-300 rounded-2xl p-12 text-center">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}
          
          {!videoFile && !videoUrl && !uploading && (
            <>
              <VideoIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Video hochladen</h3>
              <p className="text-gray-600 mb-6">Wähle eine Video-Datei aus (max. 100MB)</p>
              
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="video/*"
                  onChange={(e) => handleVideoUpload(e.target.files[0])}
                  className="hidden"
                  data-testid="video-input"
                />
                <div className="inline-block px-6 py-3 bg-brand-green hover:bg-brand-green-dark text-white font-semibold rounded-xl" data-testid="video-select-btn">
                  <Upload className="inline h-5 w-5 mr-2" />
                  Video auswählen
                </div>
              </label>
            </>
          )}

          {uploading && (
            <div className="py-8">
              <div className="relative w-24 h-24 mx-auto mb-4">
                <svg className="w-24 h-24 transform -rotate-90">
                  <circle
                    cx="48"
                    cy="48"
                    r="40"
                    stroke="#e5e7eb"
                    strokeWidth="8"
                    fill="none"
                  />
                  <circle
                    cx="48"
                    cy="48"
                    r="40"
                    stroke="#22c55e"
                    strokeWidth="8"
                    fill="none"
                    strokeDasharray={`${2 * Math.PI * 40}`}
                    strokeDashoffset={`${2 * Math.PI * 40 * (1 - uploadProgress / 100)}`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-xl font-bold text-brand-green">{uploadProgress}%</span>
                </div>
              </div>
              <p className="text-gray-600">Video wird hochgeladen...</p>
              <p className="text-sm text-gray-500 mt-2">Bitte warten Sie, bis der Upload abgeschlossen ist</p>
            </div>
          )}

          {(videoFile || videoUrl) && !uploading && (
            <div className="py-4">
              <div className="w-16 h-16 bg-brand-green/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="h-8 w-8 text-brand-green" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Video hochgeladen</h3>
              <p className="text-gray-600 mb-4">{videoFile?.name || 'video_rundgang.mp4'}</p>
              
              <button
                onClick={() => {
                  setVideoFile(null);
                  setVideoUrl('');
                }}
                className="text-sm text-gray-600 hover:text-gray-900 underline"
              >
                Anderes Video hochladen
              </button>
            </div>
          )}
        </div>

        {/* Buttons */}
        <div className="flex justify-between items-center mt-12">
          <Button
            onClick={() => navigate(`/portal/bilder-upload?case_id=${caseId}&partner_id=${partnerId}`)}
            variant="outline"
            className="border-2"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Zurück zu Fotos
          </Button>

          {!videoFile && !videoUrl && (
            <div className="text-center">
              <p className="text-sm text-orange-600 mb-2">
                Video-Rundgang ist Pflicht
              </p>
              <Button disabled className="bg-gray-300 text-gray-600 px-8">
                Weiter zu Dokumenten
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          )}

          {(videoFile || videoUrl) && (
            <Button
              onClick={handleWeiter}
              className="bg-brand-green hover:bg-brand-green-dark text-white px-8"
            >
              Weiter zu Dokumenten
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>
      </main>
    </div>
  );
};

export default VideoUploadPage;
